import { useState, useMemo, useCallback, useEffect } from 'react';
import { usePersistedFilterState } from '@/hooks/usePersistedFilterState';
import { FilterRule, FilterField, FilterGroup, FilterEntity, FilterAttribute } from '@/types/filters';
import { Project } from './useProjectData';
import { supabase } from '@/integrations/supabase/client';
import { getCustomerDisplayName } from '@/utils/customerDisplayHelpers';

type Option = { value: string; label: string };

export const useProjectFilterBuilder = (projects: Project[]) => {
  const [draftGroups, setDraftGroups] = usePersistedFilterState<FilterGroup[]>('filters-projects-draft', []);
  const [appliedGroups, setAppliedGroups] = usePersistedFilterState<FilterGroup[]>('filters-projects-applied', []);

  const [optionsOverride, setOptionsOverride] = useState<{
    customers: Option[];
    leadSources: Option[];
    leadOwners: Option[];
    createdBy: Option[];
    cities: Option[];
    provinces: Option[];
    suburbs: Option[];
    tags: Option[];
    projectStatuses: Option[];
  }>({ 
    customers: [], 
    leadSources: [], 
    leadOwners: [], 
    createdBy: [],
    cities: [], 
    provinces: [], 
    suburbs: [], 
    tags: [],
    projectStatuses: []
  });

  // Fetch comprehensive filter options from database
  useEffect(() => {
    const fetchOptions = async () => {
      try {
        // Fetch customers, projects, and profiles in parallel
        const [customersRes, projectsRes, profilesRes] = await Promise.all([
          supabase.from('customers').select('id, name, last_name, customer_type, business_name, representative_first_name, representative_last_name, lead_source, suburb, city, province_state, tags, lead_owner_id'),
          supabase.from('projects').select('id, name, status'),
          supabase.from('profiles').select('id, first_name, last_name')
        ]);

        if (customersRes.error) throw customersRes.error;
        if (projectsRes.error) throw projectsRes.error;
        if (profilesRes.error) throw profilesRes.error;

        // Process customers data
        const leadSourcesSet = new Set<string>();
        const suburbsSet = new Set<string>();
        const citiesSet = new Set<string>();
        const provincesSet = new Set<string>();
        const tagsSet = new Set<string>();
        const ownerIdsSet = new Set<string>();

        (customersRes.data || []).forEach((c: any) => {
          if (c.lead_source) leadSourcesSet.add(c.lead_source);
          if (c.suburb) suburbsSet.add(c.suburb);
          if (c.city) citiesSet.add(c.city);
          if (c.province_state) provincesSet.add(c.province_state);
          if (Array.isArray(c.tags)) c.tags.filter(Boolean).forEach((t: string) => tagsSet.add(t));
          if (c.lead_owner_id) ownerIdsSet.add(c.lead_owner_id);
        });

        // Create options and sort alphabetically
        const customerOptions = (customersRes.data || []).map((c: any) => ({
          value: c.id,
          label: getCustomerDisplayName(c)
        })).sort((a, b) => a.label.localeCompare(b.label));

        const projectStatuses = [...new Set((projectsRes.data || []).map((p: any) => p.status).filter(Boolean))].sort();

        // Create profile options for lead owners and created by
        const profileOptions = (profilesRes.data || []).map((p: any) => ({
          value: p.id,
          label: `${p.first_name || ''} ${p.last_name || ''}`.trim() || p.id
        })).sort((a, b) => a.label.localeCompare(b.label));

        const leadOwnerOptions = profileOptions.filter(p => ownerIdsSet.has(p.value));

        setOptionsOverride({
          customers: customerOptions,
          leadSources: Array.from(leadSourcesSet).map(v => ({ value: v, label: v })).sort((a, b) => a.label.localeCompare(b.label)),
          leadOwners: leadOwnerOptions,
          createdBy: profileOptions,
          cities: Array.from(citiesSet).map(v => ({ value: v, label: v })).sort((a, b) => a.label.localeCompare(b.label)),
          provinces: Array.from(provincesSet).map(v => ({ value: v, label: v })).sort((a, b) => a.label.localeCompare(b.label)),
          suburbs: Array.from(suburbsSet).map(v => ({ value: v, label: v })).sort((a, b) => a.label.localeCompare(b.label)),
          tags: Array.from(tagsSet).map(v => ({ value: v, label: v })).sort((a, b) => a.label.localeCompare(b.label)),
          projectStatuses: projectStatuses.map(s => ({ value: s, label: s }))
        });
      } catch (err) {
        console.error('Error fetching filter options:', err);
      }
    };

    fetchOptions();
  }, []);

  // Get unique values from current projects for fallback options
  const dynamicOptions = useMemo(() => {
    const leadSources = [...new Set(
      projects
        .map(p => p.customers?.lead_source)
        .filter(Boolean)
    )].map(source => ({ value: source!, label: source! })).sort((a, b) => a.label.localeCompare(b.label));

    const customers = [...new Set(
      projects
        .map(p => p.customers ? getCustomerDisplayName(p.customers) : null)
        .filter(Boolean)
    )].map(name => ({ value: name!, label: name! })).sort((a, b) => a.label.localeCompare(b.label));

    const createdBy = [...new Set(
      projects
        .map(p => p.created_by) // Use the created_by UUID directly since we don't have profile names
        .filter(Boolean)
    )].map(id => ({ value: id!, label: `User ${id!.slice(0, 8)}...` })).sort((a, b) => a.label.localeCompare(b.label));

    const statuses = [...new Set(
      projects
        .map(p => p.status)
        .filter(Boolean)
    )].map(status => ({ value: status!, label: status! })).sort((a, b) => a.label.localeCompare(b.label));

    return {
      leadSources: optionsOverride.leadSources.length ? optionsOverride.leadSources : leadSources,
      customers: optionsOverride.customers.length ? optionsOverride.customers : customers,
      createdBy: optionsOverride.createdBy.length ? optionsOverride.createdBy : createdBy,
      cities: optionsOverride.cities,
      provinces: optionsOverride.provinces,
      suburbs: optionsOverride.suburbs,
      tags: optionsOverride.tags,
      projectStatuses: optionsOverride.projectStatuses.length ? optionsOverride.projectStatuses : statuses,
    };
  }, [projects, optionsOverride]);

  // Filter configuration - 4 main entities for projects
  const filterEntities: FilterEntity[] = [
    { key: 'project', label: 'Project' },
    { key: 'customer', label: 'Customer' },
    { key: 'timeline', label: 'Timeline' },
    { key: 'site', label: 'Site Address' }
  ];

  // Attributes per entity
  const filterAttributesByEntity: Record<string, FilterAttribute[]> = {
    project: [
      { key: 'name', label: "'s Name", type: 'text' },
      { key: 'description', label: "'s Description", type: 'text' },
      { key: 'status', label: "'s Status", type: 'select', options: dynamicOptions.projectStatuses },
      { key: 'created_by', label: "'s Created By", type: 'select', options: dynamicOptions.createdBy },
      { key: 'has_customer', label: 'Has Customer', type: 'boolean' },
      { key: 'use_separate_site_address', label: 'Uses Separate Site Address', type: 'boolean' },
      { key: 'is_overdue', label: 'Is Overdue', type: 'boolean' },
      { key: 'project_duration_days', label: "'s Duration (days)", type: 'number' }
    ],
    customer: [
      { key: 'customer_type', label: "'s Type", type: 'select', options: [
        { value: 'residential', label: 'Residential' },
        { value: 'commercial', label: 'Commercial' }
      ]},
      { key: 'customer_name', label: "'s Name", type: 'select', options: dynamicOptions.customers },
      { key: 'lead_source', label: "'s Lead Source", type: 'select', options: dynamicOptions.leadSources },
      { key: 'lead_owner', label: "'s Lead Owner", type: 'select', options: dynamicOptions.createdBy },
      { key: 'suburb', label: "'s Suburb", type: 'select', options: dynamicOptions.suburbs },
      { key: 'city', label: "'s City", type: 'select', options: dynamicOptions.cities },
      { key: 'province_state', label: "'s Province/State", type: 'select', options: dynamicOptions.provinces },
      { key: 'tags', label: "'s Tags", type: 'select', options: dynamicOptions.tags }
    ],
    timeline: [
      { key: 'created_at', label: 'Created Date', type: 'date' },
      { key: 'updated_at', label: 'Updated Date', type: 'date' },
      { key: 'expected_start_date', label: 'Expected Start Date', type: 'date' },
      { key: 'expected_end_date', label: 'Expected End Date', type: 'date' },
      { key: 'actual_start_date', label: 'Actual Start Date', type: 'date' },
      { key: 'actual_end_date', label: 'Actual End Date', type: 'date' }
    ],
    site: [
      { key: 'site_street_address', label: 'Street Address', type: 'text' },
      { key: 'site_city', label: 'City', type: 'text' },
      { key: 'site_suburb', label: 'Suburb', type: 'text' },
      { key: 'site_province_state', label: 'Province/State', type: 'text' },
      { key: 'site_postal_code', label: 'Postal Code', type: 'text' }
    ]
  };

  // Generate filter fields for UI
  const filterFields = useMemo(() => {
    const fields: FilterField[] = [];
    filterEntities.forEach(entity => {
      const attributes = filterAttributesByEntity[entity.key] || [];
      attributes.forEach(attr => {
        fields.push({
          key: `${entity.key}.${attr.key}`,
          label: `${entity.label}${attr.label}`,
          type: attr.type,
          options: attr.options
        });
      });
    });
    return fields;
  }, [dynamicOptions]);

  // Filter group management functions
  const addGroup = useCallback(() => {
    const newRule: FilterRule = {
      id: Date.now().toString(),
      entity: '',
      attribute: '',
      operator: 'equals',
      value: '',
      logic: 'AND'
    };

    const newGroup: FilterGroup = {
      id: Date.now().toString(),
      rules: [newRule],
      logic: 'AND'
    };
    setDraftGroups(prev => [...prev, newGroup]);
  }, []);

  const updateGroup = useCallback((groupId: string, updates: Partial<FilterGroup>) => {
    setDraftGroups(prev => prev.map(group => 
      group.id === groupId ? { ...group, ...updates } : group
    ));
  }, []);

  const removeGroup = useCallback((groupId: string) => {
    setDraftGroups(prev => prev.filter(group => group.id !== groupId));
  }, []);

  const addRuleToGroup = useCallback((groupId: string) => {
    const newRule: FilterRule = {
      id: Date.now().toString(),
      entity: '',
      attribute: '',
      operator: 'equals',
      value: '',
      logic: 'AND'
    };
    
    setDraftGroups(prev => prev.map(group => 
      group.id === groupId 
        ? { ...group, rules: [...group.rules, newRule] }
        : group
    ));
  }, []);

  const updateRuleInGroup = useCallback((groupId: string, ruleId: string, updates: Partial<FilterRule>) => {
    setDraftGroups(prev => prev.map(group => 
      group.id === groupId 
        ? {
            ...group,
            rules: group.rules.map(rule => 
              rule.id === ruleId ? { ...rule, ...updates } : rule
            )
          }
        : group
    ));
  }, []);

  const removeRuleFromGroup = useCallback((groupId: string, ruleId: string) => {
    setDraftGroups(prev => prev.map(group => 
      group.id === groupId 
        ? { ...group, rules: group.rules.filter(rule => rule.id !== ruleId) }
        : group
    ));
  }, []);

  const clearAllRules = useCallback(() => {
    setDraftGroups([]);
    setAppliedGroups([]);
  }, []);

  // Check if a rule is complete
  const isRuleComplete = useCallback((rule: FilterRule): boolean => {
    if (!rule.entity || !rule.attribute || !rule.operator) return false;
    
    if (rule.operator === 'is_empty' || rule.operator === 'is_not_empty') {
      return true;
    }
    
    if (rule.operator === 'between') {
      return Array.isArray(rule.value) && rule.value.length === 2 && 
             rule.value[0] !== '' && rule.value[1] !== '';
    }
    
    if (rule.operator === 'in' || rule.operator === 'not_in') {
      return Array.isArray(rule.value) && rule.value.length > 0;
    }
    
    return rule.value !== '' && rule.value !== null && rule.value !== undefined;
  }, []);

  // Apply filters
  const applyFilters = useCallback(() => {
    setAppliedGroups([...draftGroups]);
  }, [draftGroups]);

  const hasActiveFilters = appliedGroups.length > 0;
  const hasUnappliedChanges = JSON.stringify(draftGroups) !== JSON.stringify(appliedGroups);
  const canApplyFilters = draftGroups.some(group => 
    group.rules.some(rule => isRuleComplete(rule))
  );

  // Get field value from project
  const getFieldValue = useCallback((project: Project, entity: string, attribute: string): any => {
    switch (entity) {
      case 'project':
        switch (attribute) {
          case 'created_by': 
            return project.created_by; // Return the UUID directly
          default: 
            return project[attribute as keyof Project];
        }
      case 'customer':
        if (!project.customers) return null;
        switch (attribute) {
          case 'customer_type': return project.customers.customer_type;
          case 'customer_name': return getCustomerDisplayName(project.customers);
          case 'lead_owner': return project.customers.lead_owner_id;
          default: return project.customers[attribute as keyof typeof project.customers];
        }
      case 'timeline':
        return project[attribute as keyof Project];
      case 'site':
        return project[attribute as keyof Project];
      default:
        return null;
    }
  }, []);

  // Evaluate a single rule
  const evaluateRule = useCallback((project: Project, rule: FilterRule): boolean => {
    if (!isRuleComplete(rule)) return true;

    const fieldValue = getFieldValue(project, rule.entity, rule.attribute);
    const { operator, value } = rule;

    // Handle null/undefined values
    if (fieldValue === null || fieldValue === undefined) {
      return operator === 'is_empty' || operator === 'not_equals';
    }

    switch (operator) {
      case 'equals':
        if (Array.isArray(value)) {
          if (Array.isArray(fieldValue)) {
            return fieldValue.some(v => value.includes(v));
          }
          return value.includes(fieldValue);
        }
        return fieldValue == value;
      case 'not_equals':
        if (Array.isArray(value)) {
          if (Array.isArray(fieldValue)) {
            return !fieldValue.some(v => value.includes(v));
          }
          return !value.includes(fieldValue);
        }
        return fieldValue != value;
      case 'contains':
        return String(fieldValue).toLowerCase().includes(String(value).toLowerCase());
      case 'not_contains':
        return !String(fieldValue).toLowerCase().includes(String(value).toLowerCase());
      case 'greater_than':
        if (rule.attribute.includes('date') || rule.attribute.includes('created_at') || rule.attribute.includes('updated_at')) {
          return new Date(fieldValue) > new Date(value);
        } else {
          return Number(fieldValue) > Number(value);
        }
      case 'less_than':
        if (rule.attribute.includes('date') || rule.attribute.includes('created_at') || rule.attribute.includes('updated_at')) {
          return new Date(fieldValue) < new Date(value);
        } else {
          return Number(fieldValue) < Number(value);
        }
      case 'between':
        if (Array.isArray(value) && value.length === 2) {
          if (rule.attribute.includes('date') || rule.attribute.includes('created_at') || rule.attribute.includes('updated_at')) {
            const date = new Date(fieldValue);
            return date >= new Date(value[0]) && date <= new Date(value[1]);
          } else {
            const num = Number(fieldValue);
            return num >= Number(value[0]) && num <= Number(value[1]);
          }
        }
        return false;
      case 'in':
        if (Array.isArray(fieldValue)) {
          return Array.isArray(value) && fieldValue.some(item => value.includes(item));
        }
        return Array.isArray(value) && value.includes(fieldValue);
      case 'not_in':
        if (Array.isArray(fieldValue)) {
          return Array.isArray(value) && !fieldValue.some(item => value.includes(item));
        }
        return Array.isArray(value) && !value.includes(fieldValue);
      case 'is_empty':
        if (Array.isArray(fieldValue)) {
          return fieldValue.length === 0;
        }
        return !fieldValue || fieldValue === '';
      case 'is_not_empty':
        if (Array.isArray(fieldValue)) {
          return fieldValue.length > 0;
        }
        return fieldValue && fieldValue !== '';
      default:
        return true;
    }
  }, [getFieldValue, isRuleComplete]);

  // Filter projects based on applied groups
  const filteredProjects = useMemo(() => {
    if (appliedGroups.length === 0) return projects;

    return projects.filter(project => {
      return appliedGroups.every(group => {
        if (group.rules.length === 0) return true;

        const completeRules = group.rules.filter(rule => isRuleComplete(rule));
        if (completeRules.length === 0) return true;

        if (group.logic === 'OR') {
          return completeRules.some(rule => evaluateRule(project, rule));
        } else {
          return completeRules.every(rule => evaluateRule(project, rule));
        }
      });
    });
  }, [projects, appliedGroups, evaluateRule, isRuleComplete]);

  return {
    // State
    draftGroups,
    appliedGroups,
    
    // Data
    filteredProjects,
    
    // Filter configuration
    filterFields,
    filterEntities,
    filterAttributesByEntity,
    
    // Computed flags
    hasActiveFilters,
    hasUnappliedChanges,
    canApplyFilters,
    
    // Actions
    addGroup,
    updateGroup,
    removeGroup,
    addRuleToGroup,
    updateRuleInGroup,
    removeRuleFromGroup,
    clearAllRules,
    applyFilters
  };
};